import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren
} from '@angular/core';

import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ActivatedRoute } from '@angular/router';

import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';

import { Popup } from '../../../../../core/shared/components/popup/popup';
import { pageWindow } from '../../../../shared/table/table';

import {
  catchError,
  distinctUntilChanged,
  filter,
  forkJoin,
  map,
  of,
  Subscription,
  take,
  tap
} from 'rxjs';

import {
  SummaryRecommendationBlock,
  SummaryRecommendationBlockType,
  SummaryTermItem
} from '../../models/SummaryTermItem.model';

import {
  CovenantResponseDto,
  SaveCovenantResponseItem
} from '../../models/proposed-disbursement.model';

import {
  CraditService
} from '../../services/cradit.service';

import {
  Setup
} from '../../../../setup/shared/service/setup';

import {
  PickListService
} from '../../../../../core/shared/services/picklist.service';

import {
  DropdownOption,
  PKL_GRP
} from '../../../../../core/shared/models/picklist.model';

import {
  ToastService
} from '../../../../../core/shared/services/toast.service';

import { CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { RichTextEditor } from '../rich-text-editor/rich-text-editor';
import { SectionDragHandle } from '../section-drag-handle/section-drag-handle';
import { RichTextPipe } from '../../utils/rich-text.pipe';
import {
  isRichTextHtml,
  plainTextToRichText,
  richTextForSave
} from '../../utils/rich-text.util';
import { scopedListLabel } from '../../utils/scoped-numbering.util';

interface CreditTermParametersResponse {
  arrearFlg?: number;
  crdtEnsrFee?: number | null;
  crdtPrpslSeq?: number | null;
  grcPrd?: number | null;
  noOfInstlmnt?: number | null;
  prpsdExposure?: number | null;
  rpEndDt?: string | null;
  rpStrtDt?: string | null;
  rpymntFrqncy?: number | null;
  upFrntFee?: number | null;
  upfrntFlg?: number;
  crdtTrmParamsLst?: CreditFacilityTerm[];
}

interface CreditFacilityTerm {
  crdtTrmParamsSeq?: number | null;
  delFlg?: number;
  dsbEndDt?: string | null;
  dsbStrtDt?: string | null;
  dsbmntAmount?: number | null;
  grntrFlg?: number;
  kbrRstPrd?: number | null;
  rnwAblAmnt?: number | null;
  rnwAblFlg?: number;
  sprdRt?: number | null;
  typ?: number | null;
  vKbrStrt?: number | null;
  vkbrFlg?: number;
  crdtTrmParamsGrntrList?: CreditGuarantor[];
}

interface CreditGuarantor {
  crdtGrntrSeq?: number | null;
  delFlg?: number;
  grntrKey?: number | null;
  grntrNm?: string | null;
  share?: number | null;
}

type TermReferenceGroupKey =
  | 'exposure'
  | 'repayment'
  | 'fees'
  | 'facility'
  | 'disbursement'
  | 'pricing'
  | 'guarantee';

interface TermParameterReference {
  key: string;
  label: string;
  value: string;
  sortOrder: number;
  group: TermReferenceGroupKey;
}

interface TermParameterGroup {
  key: TermReferenceGroupKey;
  label: string;
  description: string;
  sortOrder: number;
  references: TermParameterReference[];
}

const TERM_REFERENCE_GROUP_META: Record<
  TermReferenceGroupKey,
  {
    label: string;
    description: string;
    sortOrder: number;
  }
> = {
  exposure: {
    label: 'Exposure',
    description:
      'Existing or proposed exposure limits.',
    sortOrder: 1,
  },

  repayment: {
    label: 'Repayment',
    description:
      'Grace period, installments, frequency and repayment dates.',
    sortOrder: 2,
  },

  fees: {
    label: 'Fees and Charges',
    description:
      'Upfront fee and credit insurance charges.',
    sortOrder: 3,
  },

  facility: {
    label: 'Facility',
    description:
      'Facility type and renewable facility information.',
    sortOrder: 4,
  },

  disbursement: {
    label: 'Disbursement',
    description:
      'Disbursement amount and disbursement dates.',
    sortOrder: 5,
  },

  pricing: {
    label: 'Pricing / KIBOR',
    description:
      'Spread rate, KIBOR start and reset period.',
    sortOrder: 6,
  },

  guarantee: {
    label: 'Guarantee / Security',
    description:
      'Guarantor name and guarantee share.',
    sortOrder: 7,
  },
};

const TERM_REFERENCE_GROUP_LABEL_KEYS: Record<
  TermReferenceGroupKey,
  string
> = {
  exposure: 'CREDIT_SECTION_SUMMARY_TERMS.GROUP_EXPOSURE',
  repayment: 'CREDIT_SECTION_SUMMARY_TERMS.GROUP_REPAYMENT',
  fees: 'CREDIT_SECTION_SUMMARY_TERMS.GROUP_FEES',
  facility: 'CREDIT_SECTION_SUMMARY_TERMS.GROUP_FACILITY',
  disbursement: 'CREDIT_SECTION_SUMMARY_TERMS.GROUP_DISBURSEMENT',
  pricing: 'CREDIT_SECTION_SUMMARY_TERMS.GROUP_PRICING',
  guarantee: 'CREDIT_SECTION_SUMMARY_TERMS.GROUP_GUARANTEE',
};

interface ActiveRecommendationEditor {
  item: SummaryTermItem;
  blockIndex: number;
  selectionStart: number;
  selectionEnd: number;
}

/**
 * A Covenants question row (picklist group 5) enriched with its
 * Question Type (picklist group 20) answer widget. `checked` /
 * `crdtCnvntQstnSeq` only matter inside the selection popup; the row
 * is also stored as-is on the Summary Terms item for the read-only
 * Answer column shown in the main list.
 */
interface CovenantQuestionRow {
  crdtCnvntQstnSeq: number | null;
  qstSeq: number;
  qstStr: string;
  checked: boolean;
  qstTypKey: number;
  inputType: 'text' | 'checkbox' | 'radio' | 'select';
  answerOptions: { answrSeq: number; answrStr: string }[];
  answerValue: string;
  answerChecked: boolean;
  remarks: string;

  /** Null until `/crdt-prpsl/save-covenant-responses` has saved this once. */
  crdtCnvntRspSeq: number | null;
}

@Component({
  selector: 'app-summary-terms-section',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    FontAwesomeModule,
    Popup,
    TranslatePipe,
    RichTextEditor,
    RichTextPipe,
    CdkDropList,
    CdkDrag,
    CdkDragHandle,
    SectionDragHandle
  ],
  templateUrl: './summary-terms-section.html',
  styleUrl: './summary-terms-section.css',
})
export class SummaryTermsSection
  implements OnInit, OnDestroy {

  @Input() items: SummaryTermItem[] = [];
  @Input() editable = false;

  onboardingId = 0;
  crdtPrpslSeq: number | null = null;

  termParameterReferences: TermParameterReference[] = [];
  termParameterGroups: TermParameterGroup[] = [];

  /**
   * One selected Credit Term reference per visible Summary row.
   * The key is the row index. This is UI-only and is not persisted.
   */
  selectedReferenceKeyByRow: Record<number, string> = {};

  /**
   * Picklist group 44: Summary Terms Parameters.
   * User can select one of these values and add it as a row.
   */
  summaryTermOptions: DropdownOption[] = [];
  selectedSummaryTermCode: number | null = null;

  previousMaxExposure: number | null = null;

  loadingSummaryTerms = false;
  loadingReferenceData = false;
  saving = false;

  successMessage = '';
  errorMessage = '';

  showDeletePopup = false;
  deleteMessage = '';

  /**
   * Covenants (refCdSeq 2675) uses a dedicated popup flow
   * instead of the regular Add Selected row flow.
   */
  showCovenantsPopup = false;
  private readonly COVENANTS_REF_CD_SEQ = 2675;
  private readonly COVENANTS_QSTNR_SEQ = 5;

  covenantQuestions: CovenantQuestionRow[] = [];
  covenantSearchTerm = '';
  faMagnifyingGlass = faMagnifyingGlass;

  @ViewChildren(RichTextEditor)
  private recommendationRichEditors?: QueryList<RichTextEditor>;

  covenantPageSize = 10;
  covenantCurrentPage = 1;

  loadingCovenantQuestions = false;
  covenantQuestionsError = '';
  savingCovenantQuestions = false;
  savingCovenantResponses = false;

  /**
   * Answer + remarks of each covenant question as last saved through
   * "Save Covenants" (or loaded from the server), keyed by qstSeq.
   * "Save and Continue" is blocked while any question differs from this.
   */
  private savedCovenantAnswers = new Map<number, string>();

  /** Picklist group 20 (Question Type) labels, keyed by refCdSeq. */
  private questionTypeMap: Record<number, string> = {};
  private pendingDeleteType:
    'row' | 'block' | 'covenantQuestion' | null = null;
  private pendingDeleteRowIndex: number | null = null;
  private pendingDeleteItem: SummaryTermItem | null = null;
  private pendingDeleteBlockIndex: number | null = null;
  private pendingDeleteCovenantQuestion:
    NonNullable<SummaryTermItem['covenantQuestions']>[number] | null = null;

  private rawTermsResponse:
    CreditTermParametersResponse | null = null;

  private frequencyOptions: DropdownOption[] = [];
  private termParameterOptions: DropdownOption[] = [];

  private deletedRows: SummaryTermItem[] = [];

  private activeRecommendationEditor:
    ActiveRecommendationEditor | null = null;

  private subscriptions = new Subscription();

  constructor(
    private creditService: CraditService,
    private route: ActivatedRoute,
    private pickListService: PickListService,
    private toast: ToastService,
    private setup: Setup,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.onboardingId = Number(
      this.findRouteParameter([
        'id',
        'oid'
      ])
    );

    this.loadRequiredPicklists();
    this.listenForProposalSequence();
    this.listenForSaveTrigger();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private listenForProposalSequence(): void {
    const sequenceSubscription =
      this.creditService.crdtPrpslSeq$
        .pipe(distinctUntilChanged())
        .subscribe((sequence) => {
          this.crdtPrpslSeq = sequence;

          if (
            sequence !== null &&
            sequence > 0
          ) {
            this.loadSummaryTerms();
            this.loadTermReferenceData();
          } else {
            this.items = [];
            this.termParameterReferences = [];
            this.termParameterGroups = [];
            this.selectedReferenceKeyByRow = {};
            this.previousMaxExposure = null;
          }
        });

    this.subscriptions.add(
      sequenceSubscription
    );
  }

  private listenForSaveTrigger(): void {
    const saveSubscription =
      this.creditService
        .saveSectionTrigger$
        .pipe(
          filter(
            (sectionId: string) =>
              sectionId === 'summary-terms'
          )
        )
        .subscribe(() => {
          this.saveSummaryTerms();
        });

    this.subscriptions.add(
      saveSubscription
    );
  }

  private loadRequiredPicklists(): void {
    const groups = [
      PKL_GRP.SUMMARY_TERMS_PARAMETERS,
      PKL_GRP.TERMS_PARAMETERS,
      PKL_GRP.DISBURSEMENT_FREQUENCY
    ];

    const picklistSubscription =
      this.pickListService
        .loadCodesAsync(groups)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.summaryTermOptions =
              this.getSortedActiveOptions(
                PKL_GRP.SUMMARY_TERMS_PARAMETERS
              );

            this.termParameterOptions =
              this.getSortedActiveOptions(
                PKL_GRP.TERMS_PARAMETERS
              );

            this.frequencyOptions =
              this.getSortedActiveOptions(
                PKL_GRP.DISBURSEMENT_FREQUENCY
              );

            /*
             * Old saved Summary Terms rows may not contain
             * the new group-44 code. Match them by label.
             */
            this.attachMissingPicklistCodes();

            this.buildTermParameterReferences();
          },

          error: (error) => {
            console.error(
              'Summary Terms picklist load error:',
              error
            );

            this.errorMessage =
              this.translate.instant('CREDIT_SECTION_SUMMARY_TERMS.PICKLIST_LOAD_FAILED');
          }
        });

    this.subscriptions.add(
      picklistSubscription
    );
  }

  private getSortedActiveOptions(
    groupCode: number
  ): DropdownOption[] {
    return this.pickListService
      .getOptionsSync(groupCode)
      .filter(
        (option) =>
          option.original?.refCdActvFlg !== false &&
          option.original?.delFlg !== true
      )
      .sort(
        (first, second) =>
          Number(
            first.original?.sortOrder ?? 0
          ) -
          Number(
            second.original?.sortOrder ?? 0
          )
      );
  }


  loadTermReferenceData(): void {
    if (!this.crdtPrpslSeq) {
      return;
    }

    this.loadingReferenceData = true;

    const referenceSubscription =
      forkJoin({
        terms:
          this.creditService
            .getCreditTermParameters(
              this.crdtPrpslSeq
            )
            .pipe(
              catchError((error) => {
                console.error(
                  'Terms Parameters load error:',
                  error
                );

                return of([]);
              })
            ),

        previousExposure:
          this.creditService
            .getPreviousMaxExposure(
              this.crdtPrpslSeq
            )
            .pipe(
              catchError((error) => {
                console.error(
                  'Previous Max Exposure load error:',
                  error
                );

                return of(null);
              })
            )
      }).subscribe({
        next: (response) => {
          this.rawTermsResponse =
            this.extractTermParameterResponse(
              response.terms
            );

          this.previousMaxExposure =
            this.extractPreviousMaxExposure(
              response.previousExposure
            );

          this.buildTermParameterReferences();

          this.loadingReferenceData = false;
        },
        error: (error) => {
          this.loadingReferenceData = false;

          console.error(
            'Reference data error:',
            error
          );
        }
      });

    this.subscriptions.add(
      referenceSubscription
    );
  }

  loadSummaryTerms(): void {
    if (!this.crdtPrpslSeq) {
      this.items = [];
      return;
    }

    this.loadingSummaryTerms = true;
    this.errorMessage = '';

    const loadSubscription =
      this.creditService
        .getCrdtBlck(
          this.crdtPrpslSeq,
          [2682]
        )
        .subscribe({
          next: (response: any) => {
            const rows =
              this.extractResponseList(
                response
              );

            if (!rows.length) {
              this.items = [];
              this.deletedRows = [];
              this.loadingSummaryTerms = false;
              this.loadSavedCovenantsSummaryRow();
              return;
            }

            this.items = rows
              .filter(
                (row: any) =>
                  Number(row?.delFlg ?? 0) !== 1
              )
              .sort(
                (first: any, second: any) =>
                  Number(first?.sortOrdr ?? 0) -
                  Number(second?.sortOrdr ?? 0)
              )
              .map(
                (
                  row: any,
                  index: number
                ): SummaryTermItem => {
                  const data =
                    this.parseJsonData(
                      row?.jsonData
                    );

                  const description =
                    String(
                      data?.description ?? ''
                    ).trim();

                  const savedCode =
                    this.toNullableNumber(
                      data?.code
                    );

                  const matchedOption =
                    savedCode !== null
                      ? this.summaryTermOptions.find(
                          (option) =>
                            Number(option.value) ===
                            savedCode
                        )
                      : this.findSummaryTermOptionByLabel(
                          description
                        );

                  return {
                    serialNo:
                      Number(
                        data?.serialNo
                      ) ||
                      index + 1,

                    code:
                      savedCode ??
                      (
                        matchedOption
                          ? Number(
                              matchedOption.value
                            )
                          : null
                      ),

                    description:
                      description ||
                      matchedOption?.label ||
                      '',

                    expanded: false,

                    crdtPrpslBlkSeq:
                      row?.crdtPrpslBlkSeq ??
                      null,

                    recommendationBlocks:
                      this.mapRecommendationBlocks(
                        data?.recommendationBlocks
                      ),

                    validationError: ''
                  };
                }
              );

            this.renumberRows();
            this.attachMissingPicklistCodes();
            this.loadingSummaryTerms = false;
            this.loadSavedCovenantsSummaryRow();
          },
          error: (error) => {
            this.loadingSummaryTerms = false;

            this.errorMessage =
              error?.error?.message ??
              this.translate.instant('CREDIT_SECTION_SUMMARY_TERMS.LOAD_FAILED');

            console.error(
              'Summary Terms load error:',
              error
            );
          }
        });

    this.subscriptions.add(
      loadSubscription
    );
  }

  saveSummaryTerms(): void {
    this.successMessage = '';
    this.errorMessage = '';

    const proposalSequence =
      this.crdtPrpslSeq ??
      this.creditService.getCrdtPrpslSeq();

    if (!proposalSequence) {
      this.errorMessage =
        this.translate.instant('CREDIT_SECTION.PROPOSAL_SEQ_UNAVAILABLE');

      this.creditService.reportSectionSaveResult({
        sectionId: 'summary-terms',
        status: 'validation-error',
        message: this.errorMessage,
      });

      return;
    }

    // Covenants are mandatory: at least one covenant question must be added.
    const hasCovenants = this.items.some(
      (row) => this.isCovenantsRow(row) && !!row.covenantQuestions?.length
    );

    if (!hasCovenants) {
      this.errorMessage =
        this.translate.instant('CREDIT_SECTION_SUMMARY_TERMS.COVENANTS_REQUIRED');

      this.toast.error(this.errorMessage);

      this.creditService.reportSectionSaveResult({
        sectionId: 'summary-terms',
        status: 'validation-error',
        message: this.errorMessage,
      });

      return;
    }

    if (!this.validateRows(this.creditService.isSaveAndContinue)) {
      this.errorMessage =
        this.items.find((row) => row.validationError)?.validationError ??
        this.translate.instant('CREDIT_SECTION_SUMMARY_TERMS.COMPLETE_HIGHLIGHTED_ROWS');

      this.toast.error(this.errorMessage);

      this.creditService.reportSectionSaveResult({
        sectionId: 'summary-terms',
        status: 'validation-error',
        message: this.errorMessage,
      });

      return;
    }

    const activeBlockList =
      this.items.map(
        (
          row: SummaryTermItem,
          index: number
        ) => ({
          crdtPrpslBlkSeq:
            row.crdtPrpslBlkSeq ?? null,

          refGrp:
            PKL_GRP.SUMMARY_TITLE,

          refValSeq: 2682,

          blkTyp: 3,

          delFlg: 0,

          sortOrdr: index + 1,

          txtData: '',

          jsonData: [
            {
              serialNo: index + 1,

              code:
                this.toNullableNumber(
                  row.code
                ),

              description:
                row.description.trim(),

              recommendationBlocks:
                row.recommendationBlocks
                  .filter(
                    (
                      block:
                        SummaryRecommendationBlock
                    ) =>
                      block.content.trim()
                  )
                  .map(
                    (
                      block:
                        SummaryRecommendationBlock
                    ) => ({
                      type: block.type,

                      items:
                        block.type ===
                        'paragraph'
                          ? []
                          : this.getLines(
                              block.content
                            ),

                      content:
                        block.type ===
                        'paragraph'
                          ? richTextForSave(
                              block.content
                            ).trim()
                          : ''
                    })
                  )
            }
          ]
        })
      );

    const deletedBlockList =
      this.deletedRows
        .filter(
          (row) =>
            row.crdtPrpslBlkSeq !== null
        )
        .map((row) => ({
          crdtPrpslBlkSeq:
            row.crdtPrpslBlkSeq,

          refGrp:
            PKL_GRP.SUMMARY_TITLE,

          refValSeq: 2682,

          blkTyp: 3,

          delFlg: 1,

          sortOrdr: 0,

          txtData: '',

          jsonData: [
            {
              serialNo: row.serialNo,
              code:
                this.toNullableNumber(
                  row.code
                ),
              description: row.description,
              recommendationBlocks: []
            }
          ]
        }));

    const payload = {
      crprtOnbrdngSeq:
        this.onboardingId,

      crdtPrpslSeq:
        proposalSequence,

      blockList: [
        ...activeBlockList,
        ...deletedBlockList
      ]
    };

    console.log(
      'Summary Terms payload:',
      payload
    );

    this.saving = true;

    const saveSubscription =
      this.creditService
        .saveSection(payload)
        .subscribe({
          next: (response: any) => {
            const returnedSequence =
              Number(
                response?.crdtPrpslSeq ??
                response?.data?.crdtPrpslSeq ??
                0
              );

            if (returnedSequence > 0) {
              this.creditService
                .setCrdtPrpslSeq(
                  returnedSequence
                );
            }

            this.deletedRows = [];
            this.saving = false;

            this.successMessage =
              this.translate.instant('CREDIT_SECTION_SUMMARY_TERMS.SAVED');

            this.toast.success(this.successMessage);

            this.loadSummaryTerms();

            this.creditService.reportSectionSaveResult({
              sectionId: 'summary-terms',
              status: 'saved',
            });
          },
          error: (error) => {
            this.saving = false;

            this.errorMessage =
              error?.error?.message ??
              this.translate.instant('CREDIT_SECTION_SUMMARY_TERMS.SAVE_FAILED');

            console.error(
              'Summary Terms save error:',
              error
            );

            this.creditService.reportSectionSaveResult({
              sectionId: 'summary-terms',
              status: 'api-error',
              message: this.errorMessage,
            });
          }
        });

    this.subscriptions.add(
      saveSubscription
    );
  }

  get availableSummaryTermOptions():
    DropdownOption[] {
    const usedCodes =
      new Set(
        this.items
          .map(
            (item) =>
              this.toNullableNumber(
                item.code
              )
          )
          .filter(
            (
              value
            ): value is number =>
              value !== null
          )
      );

    return this.summaryTermOptions.filter(
      (option) =>
        Number(option.value) ===
          this.COVENANTS_REF_CD_SEQ ||
        !usedCodes.has(
          Number(option.value)
        )
    );
  }

  get filteredCovenantQuestions(): CovenantQuestionRow[] {
    const term =
      this.covenantSearchTerm.trim().toLowerCase();

    if (!term) {
      return this.covenantQuestions;
    }

    return this.covenantQuestions.filter(
      (question) =>
        question.qstStr.toLowerCase().includes(term)
    );
  }

  get covenantTotalRecords(): number {
    return this.filteredCovenantQuestions.length;
  }

  get covenantStartRecord(): number {
    return this.covenantTotalRecords === 0
      ? 0
      : (this.covenantCurrentPage - 1) * this.covenantPageSize + 1;
  }

  get covenantEndRecord(): number {
    return Math.min(
      this.covenantCurrentPage * this.covenantPageSize,
      this.covenantTotalRecords
    );
  }

  get paginatedCovenantQuestions(): CovenantQuestionRow[] {
    const start =
      (this.covenantCurrentPage - 1) * this.covenantPageSize;

    return this.filteredCovenantQuestions.slice(
      start,
      start + this.covenantPageSize
    );
  }

  onCovenantSearchChange(): void {
    this.covenantCurrentPage = 1;
  }

  get covenantPages(): (number | null)[] {
    return pageWindow(this.covenantCurrentPage, Math.ceil(this.covenantTotalRecords / this.covenantPageSize));
  }

  goToCovenantPage(page: number): void {
    if (page >= 1 && page <= Math.ceil(this.covenantTotalRecords / this.covenantPageSize)) {
      this.covenantCurrentPage = page;
    }
  }

  nextCovenantPage(): void {
    if (this.covenantEndRecord < this.covenantTotalRecords) {
      this.covenantCurrentPage++;
    }
  }

  prevCovenantPage(): void {
    if (this.covenantCurrentPage > 1) {
      this.covenantCurrentPage--;
    }
  }

  addSelectedSummaryTerm(): void {
    const selectedCode =
      this.toNullableNumber(
        this.selectedSummaryTermCode
      );

    if (selectedCode === null) {
      this.errorMessage =
        this.translate.instant('CREDIT_SECTION_SUMMARY_TERMS.SELECT_PARAMETER_FIRST');
      return;
    }

    if (selectedCode === this.COVENANTS_REF_CD_SEQ) {
      this.openCovenantsPopup();
      return;
    }

    const option =
      this.summaryTermOptions.find(
        (item) =>
          Number(item.value) ===
          selectedCode
      );

    if (!option) {
      this.errorMessage =
        this.translate.instant('CREDIT_SECTION_SUMMARY_TERMS.PARAMETER_NOT_FOUND');
      return;
    }

    const alreadyExists =
      this.items.some(
        (item) =>
          this.toNullableNumber(
            item.code
          ) === selectedCode
      );

    if (alreadyExists) {
      this.errorMessage =
        this.translate.instant('CREDIT_SECTION_SUMMARY_TERMS.ALREADY_ADDED', { label: option.label });
      return;
    }

    this.items.push(
      this.createRow(
        this.items.length + 1,
        option.label,
        this.getDefaultBlockType(
          option.label
        ),
        selectedCode
      )
    );

    this.selectedSummaryTermCode = null;
    this.errorMessage = '';

    this.renumberRows();
  }

  private openCovenantsPopup(): void {
    this.showCovenantsPopup = true;
    this.covenantSearchTerm = '';
    this.covenantCurrentPage = 1;
    this.loadCovenantQuestions();
  }

  private loadCovenantQuestions(): void {
    this.loadingCovenantQuestions = true;
    this.covenantQuestionsError = '';

    const questionTypeSubscription =
      this.ensureQuestionTypeMap()
        .subscribe(() => this.fetchCovenantQuestions());

    this.subscriptions.add(
      questionTypeSubscription
    );
  }

  private fetchCovenantQuestions(): void {
    const proposalSequence =
      this.crdtPrpslSeq ??
      this.creditService.getCrdtPrpslSeq();

    const questionsSubscription =
      forkJoin({
        questions:
          this.setup
            .getActQuestionList(this.COVENANTS_QSTNR_SEQ),

        savedQuestions:
          proposalSequence
            ? this.creditService
                .getCovenantQuestions(proposalSequence)
                .pipe(
                  catchError((error) => {
                    console.error(
                      'Saved covenant questions load error:',
                      error
                    );

                    return of([]);
                  })
                )
            : of([])
      }).subscribe({
        next: (response) => {
          const questions: any[] =
            (Array.isArray(response.questions) ? response.questions : [])
              .filter((question) => !question?.delFlg);

          const savedQuestions =
            (Array.isArray(response.savedQuestions) ? response.savedQuestions : [])
              .filter((saved) => Number(saved?.delFlg ?? 0) !== 1);

          const savedSeqs =
            new Set(
              savedQuestions.map(
                (saved) => Number(saved?.qstnSeq)
              )
            );

          /*
           * Already-selected questions are shown on the Summary
           * Terms row itself, so this picker only lists the ones
           * not added yet. A removed question (row deleted, or
           * saved with delFlg 1) drops out of `savedQuestions`
           * above and reappears here automatically.
           */
          this.covenantQuestions =
            questions
              .filter(
                (question) =>
                  !savedSeqs.has(Number(question.qstSeq))
              )
              .map((question) =>
                this.buildCovenantRow(
                  question,
                  null,
                  false
                )
              );

          this.loadingCovenantQuestions = false;

          const answerOptionsSubscription =
            this.loadCovenantAnswerOptions(
              this.covenantQuestions
            ).subscribe();

          this.subscriptions.add(
            answerOptionsSubscription
          );
        },
        error: (error) => {
          this.loadingCovenantQuestions = false;

          this.covenantQuestionsError =
            this.translate.instant('CREDIT_SECTION_SUMMARY_TERMS.COVENANT_LIST_LOAD_FAILED');

          console.error(
            'Covenants question list load error:',
            error
          );
        }
      });

    this.subscriptions.add(
      questionsSubscription
    );
  }

  /**
   * Loads picklist group 20 (Question Type) into `questionTypeMap`
   * once, so the Answer widget can be resolved for each question.
   */
  private ensureQuestionTypeMap() {
    if (Object.keys(this.questionTypeMap).length) {
      return of(void 0);
    }

    return this.pickListService
      .loadCodesAsync([PKL_GRP.QUESTION_TYPE])
      .pipe(
        take(1),
        tap(() => {
          this.questionTypeMap = {};

          for (const option of this.pickListService.getOptionsSync(
            PKL_GRP.QUESTION_TYPE
          )) {
            this.questionTypeMap[Number(option.value)] = option.label;
          }
        }),
        map(() => void 0),
        catchError(() => of(void 0))
      );
  }

  private buildCovenantRow(
    question: any,
    crdtCnvntQstnSeq: number | null,
    checked: boolean
  ): CovenantQuestionRow {
    const qstTypKey = Number(question.qstTypKey);

    return {
      crdtCnvntQstnSeq,
      qstSeq: Number(question.qstSeq),
      qstStr: String(question.qstStr ?? '').trim(),
      checked,
      qstTypKey,
      inputType:
        this.resolveCovenantAnswerInputType(qstTypKey),
      answerOptions: [],
      answerValue: '',
      answerChecked: false,
      remarks: '',
      crdtCnvntRspSeq: null
    };
  }

  /**
   * Picklist group 20 (Question Type) is setup-driven, so the input
   * widget for the Answer column is inferred from the label text
   * rather than a hardcoded refCdSeq.
   */
  private resolveCovenantAnswerInputType(
    qstTypKey: number
  ): 'text' | 'checkbox' | 'radio' | 'select' {
    const label =
      (this.questionTypeMap[qstTypKey] || '').toLowerCase();

    if (label.includes('text')) {
      return 'text';
    }

    if (label.includes('check')) {
      return 'checkbox';
    }

    if (label.includes('radio')) {
      return 'radio';
    }

    return 'select';
  }

  private loadCovenantAnswerOptions(
    rows: CovenantQuestionRow[]
  ) {
    const rowsNeedingOptions =
      rows.filter(
        (row) =>
          row.inputType === 'select' ||
          row.inputType === 'radio'
      );

    if (!rowsNeedingOptions.length) {
      return of(void 0);
    }

    const answerCalls =
      rowsNeedingOptions.map((row) =>
        this.setup
          .getAnswerList(row.qstSeq)
          .pipe(
            catchError(() => of([]))
          )
      );

    return forkJoin(answerCalls).pipe(
      tap((answersPerQuestion: any[][]) => {
        rowsNeedingOptions.forEach((row, index) => {
          row.answerOptions =
            (answersPerQuestion[index] || [])
              .filter((answer: any) => !answer?.delFlg)
              .map((answer: any) => ({
                answrSeq: Number(answer.answrSeq),
                answrStr: String(answer.answrStr ?? '')
              }));
        });
      }),
      map(() => void 0),
      catchError((error) => {
        console.error(
          'Covenant answer options load error:',
          error
        );

        return of(void 0);
      })
    );
  }

  saveCovenantQuestions(): void {
    const proposalSequence =
      this.crdtPrpslSeq ??
      this.creditService.getCrdtPrpslSeq();

    if (!proposalSequence) {
      this.toast.error(
        'CREDIT_SECTION.PROPOSAL_SEQ_UNAVAILABLE'
      );
      return;
    }

    const activeQuestions =
      this.covenantQuestions
        .filter((question) => question.checked)
        .map((question) => ({
          crdtCnvntQstnSeq: question.crdtCnvntQstnSeq,
          qstnSeq: question.qstSeq,
          delFlg: 0
        }));

    /*
     * A question that was already saved (has a crdtCnvntQstnSeq) but got
     * unchecked in this session must go back with delFlg: 1 so the
     * backend removes it — it is not simply dropped from the payload.
     */
    const removedQuestions =
      this.covenantQuestions
        .filter(
          (question) =>
            !question.checked &&
            question.crdtCnvntQstnSeq !== null
        )
        .map((question) => ({
          crdtCnvntQstnSeq: question.crdtCnvntQstnSeq,
          qstnSeq: question.qstSeq,
          delFlg: 1
        }));

    const payloadQuestions = [
      ...activeQuestions,
      ...removedQuestions
    ];

    if (!payloadQuestions.length) {
      this.toast.error(
        'CREDIT_SECTION_SUMMARY_TERMS.SELECT_ONE_COVENANT'
      );
      return;
    }

    this.savingCovenantQuestions = true;

    const saveSubscription =
      this.creditService
        .saveCovenantQuestions({
          crdtPrpslSeq: proposalSequence,
          questions: payloadQuestions
        })
        .subscribe({
          next: () => {
            this.savingCovenantQuestions = false;

            this.toast.success(
              'CREDIT_SECTION_SUMMARY_TERMS.COVENANT_QUESTIONS_SAVED'
            );

            /*
             * Reload the Covenants row straight from the server (same
             * call used on page load) so the newly saved questions
             * show up in the table immediately, with the real
             * crdtCnvntQstnSeq values, instead of requiring a full
             * app refresh.
             */
            this.loadSavedCovenantsSummaryRow();

            this.showCovenantsPopup = false;
          },
          error: (error) => {
            this.savingCovenantQuestions = false;

            console.error(
              'Save covenant questions error:',
              error
            );

            this.toast.error(
              error?.error?.message ??
                'CREDIT_SECTION_SUMMARY_TERMS.COVENANT_QUESTIONS_SAVE_FAILED'
            );
          }
        });

    this.subscriptions.add(
      saveSubscription
    );
  }

  isCovenantsRow(item: SummaryTermItem): boolean {
    return (
      this.toNullableNumber(item.code) ===
      this.COVENANTS_REF_CD_SEQ
    );
  }

  covenantListPageSize = 10;
  covenantListCurrentPage = 1;

  covenantListTotalRecords(item: SummaryTermItem): number {
    return item.covenantQuestions?.length ?? 0;
  }

  covenantListStartRecord(item: SummaryTermItem): number {
    const total = this.covenantListTotalRecords(item);

    return total === 0
      ? 0
      : (this.covenantListCurrentPage - 1) * this.covenantListPageSize + 1;
  }

  covenantListEndRecord(item: SummaryTermItem): number {
    return Math.min(
      this.covenantListCurrentPage * this.covenantListPageSize,
      this.covenantListTotalRecords(item)
    );
  }

  paginatedCovenantListQuestions(
    item: SummaryTermItem
  ): SummaryTermItem['covenantQuestions'] {
    const questions = item.covenantQuestions ?? [];
    const start =
      (this.covenantListCurrentPage - 1) * this.covenantListPageSize;

    return questions.slice(
      start,
      start + this.covenantListPageSize
    );
  }

  covenantListPages(item: SummaryTermItem): (number | null)[] {
    return pageWindow(this.covenantListCurrentPage, Math.ceil(this.covenantListTotalRecords(item) / this.covenantListPageSize));
  }

  goToCovenantListPage(item: SummaryTermItem, page: number): void {
    if (page >= 1 && page <= Math.ceil(this.covenantListTotalRecords(item) / this.covenantListPageSize)) {
      this.covenantListCurrentPage = page;
    }
  }

  nextCovenantListPage(item: SummaryTermItem): void {
    if (
      this.covenantListEndRecord(item) <
      this.covenantListTotalRecords(item)
    ) {
      this.covenantListCurrentPage++;
    }
  }

  prevCovenantListPage(): void {
    if (this.covenantListCurrentPage > 1) {
      this.covenantListCurrentPage--;
    }
  }

  private upsertCovenantsSummaryRow(
    questions: CovenantQuestionRow[]
  ): void {
    if (!questions.length) {
      this.items =
        this.items.filter(
          (item) => !this.isCovenantsRow(item)
        );

      this.renumberRows();
      return;
    }

    const option =
      this.summaryTermOptions.find(
        (item) =>
          Number(item.value) ===
          this.COVENANTS_REF_CD_SEQ
      );

    const description =
      (option?.label ?? 'Covenants').trim();

    const block: SummaryRecommendationBlock = {
      type: 'numbered-list',
      content:
        questions
          .map((question) => question.qstStr)
          .join('\n')
    };

    const existingIndex =
      this.items.findIndex(
        (item) =>
          this.toNullableNumber(item.code) ===
          this.COVENANTS_REF_CD_SEQ
      );

    if (existingIndex > -1) {
      this.items[existingIndex] = {
        ...this.items[existingIndex],
        description,
        recommendationBlocks: [block],
        covenantQuestions: questions
      };
    } else {
      this.items.push({
        serialNo: this.items.length + 1,
        code: this.COVENANTS_REF_CD_SEQ,
        description,
        expanded: false,
        crdtPrpslBlkSeq: null,
        recommendationBlocks: [block],
        validationError: '',
        covenantQuestions: questions
      });
    }

    this.renumberRows();
  }

  /**
   * Prefills the Covenants row's Answer/Remarks widgets from any
   * previously saved `/crdt-prpsl/save-covenant-responses`, so a
   * re-save updates the existing response (via `crdtCnvntRspSeq`)
   * instead of creating a duplicate.
   */
  private loadCovenantResponsesIntoRow(): void {
    const covenantsRow =
      this.items.find((item) => this.isCovenantsRow(item));

    const proposalSequence =
      this.crdtPrpslSeq ??
      this.creditService.getCrdtPrpslSeq();

    if (!covenantsRow?.covenantQuestions?.length || !proposalSequence) {
      return;
    }

    const responsesSubscription =
      this.creditService
        .getCovenantResponses(proposalSequence)
        .pipe(
          catchError((error) => {
            console.error(
              'Covenant responses load error:',
              error
            );

            return of([] as CovenantResponseDto[]);
          })
        )
        .subscribe((responses: any) => {
          // The endpoint returns `{ crdtPrpslSeq, currentQrtr, responses: [...] }`;
          // a bare array (older shape) is still accepted.
          const responseList: CovenantResponseDto[] =
            Array.isArray(responses)
              ? responses
              : Array.isArray(responses?.responses)
                ? responses.responses
                : [];

          const currentQuarter =
            String(responses?.currentQrtr ?? '').trim();

          const savedResponses =
            responseList
              .filter(
                (response) =>
                  Number(response?.delFlg ?? 0) !== 1
              );

          if (!savedResponses.length) {
            return;
          }

          covenantsRow.covenantQuestions!.forEach(
            (question) => {
              const matches =
                savedResponses.filter(
                  (response) =>
                    Number(response.qsntnSeq) ===
                    question.qstSeq
                );

              // One question can have a response per quarter; the save
              // updates the current quarter's, so that one wins.
              const savedMatch =
                matches.find(
                  (response) =>
                    !!currentQuarter &&
                    response.qrtr === currentQuarter
                ) ?? matches[matches.length - 1];

              if (!savedMatch) {
                return;
              }

              // null (not 0) when the question has no saved response yet,
              // so the save creates it rather than updating id 0.
              question.crdtCnvntRspSeq =
                Number(savedMatch.crdtCnvntRspSeq) > 0
                  ? Number(savedMatch.crdtCnvntRspSeq)
                  : null;

              question.remarks =
                savedMatch.rmrks ?? '';

              if (question.inputType === 'checkbox') {
                question.answerChecked =
                  Number(savedMatch.ansVal) === 1;
                return;
              }

              if (question.inputType === 'text') {
                question.answerValue =
                  String(savedMatch.ansVal ?? '');
                return;
              }

              const matchedOption =
                question.answerOptions.find(
                  (option) =>
                    option.answrSeq ===
                    Number(savedMatch.ansVal)
                );

              if (matchedOption) {
                question.answerValue =
                  matchedOption.answrStr;
              }
            }
          );

          covenantsRow.covenantQuestions!
            .filter((question) => question.crdtCnvntRspSeq)
            .forEach((question) => this.markCovenantAnswerSaved(question));
        });

    this.subscriptions.add(
      responsesSubscription
    );
  }

  private covenantAnswerSnapshot(
    question: NonNullable<SummaryTermItem['covenantQuestions']>[number]
  ): string {
    return JSON.stringify([
      this.resolveCovenantAnswerCode(question),
      question.remarks?.trim() ?? ''
    ]);
  }

  private markCovenantAnswerSaved(
    question: NonNullable<SummaryTermItem['covenantQuestions']>[number]
  ): void {
    this.savedCovenantAnswers.set(
      question.qstSeq,
      this.covenantAnswerSnapshot(question)
    );
  }

  /** True when any covenant answer was never saved or changed since "Save Covenants". */
  private hasUnsavedCovenantAnswers(item: SummaryTermItem): boolean {
    return (item.covenantQuestions ?? []).some(
      (question) =>
        this.savedCovenantAnswers.get(question.qstSeq) !==
        this.covenantAnswerSnapshot(question)
    );
  }

  /**
   * A checkbox question has no empty state (unchecked is a valid "No"),
   * so only text/select/radio answers are required before saving.
   */
  private isCovenantAnswerMissing(
    question: NonNullable<SummaryTermItem['covenantQuestions']>[number]
  ): boolean {
    if (question.inputType === 'checkbox') {
      return false;
    }

    return !question.answerValue?.trim();
  }

  /**
   * Answer widgets store the option's display text (`answrStr`), but
   * `save-covenant-responses` expects the option's ref code
   * (`answrSeq`) as `ansVal`.
   */
  private resolveCovenantAnswerCode(
    question: NonNullable<SummaryTermItem['covenantQuestions']>[number]
  ): number | string | null {
    if (question.inputType === 'checkbox') {
      return question.answerChecked ? 1 : 0;
    }

    if (question.inputType === 'text') {
      return question.answerValue.trim() || null;
    }

    const matchedOption =
      question.answerOptions.find(
        (option) => option.answrStr === question.answerValue
      );

    return matchedOption ? matchedOption.answrSeq : null;
  }

  saveCovenantResponses(item: SummaryTermItem): void {
    const proposalSequence =
      this.crdtPrpslSeq ??
      this.creditService.getCrdtPrpslSeq();

    if (!proposalSequence) {
      this.toast.error(
        'CREDIT_SECTION.PROPOSAL_SEQ_UNAVAILABLE'
      );
      return;
    }

    const questions = item.covenantQuestions ?? [];

    if (!questions.length) {
      this.toast.error(
        'CREDIT_SECTION_SUMMARY_TERMS.NO_COVENANTS_TO_SAVE'
      );
      return;
    }

    const hasMissingAnswer =
      questions.some(
        (question) => this.isCovenantAnswerMissing(question)
      );

    if (hasMissingAnswer) {
      this.toast.error(
        'CREDIT_SECTION_SUMMARY_TERMS.ANSWER_ALL_COVENANTS'
      );
      return;
    }

    const responses: SaveCovenantResponseItem[] =
      questions.map((question) => ({
        crdtCnvntRspSeq: question.crdtCnvntRspSeq,
        qsntnSeq: question.qstSeq,
        ansVal:
          this.resolveCovenantAnswerCode(question),
        stsKey: null,
        rmrks: question.remarks?.trim() ?? '',
        delFlg: 0
      }));

    this.savingCovenantResponses = true;

    const saveResponsesSubscription =
      this.creditService
        .saveCovenantResponses({
          crdtPrpslSeq: proposalSequence,
          responses
        })
        .subscribe({
          next: () => {
            this.savingCovenantResponses = false;

            questions.forEach(
              (question) => this.markCovenantAnswerSaved(question)
            );

            this.toast.success(
              'CREDIT_SECTION_SUMMARY_TERMS.COVENANT_RESPONSES_SAVED'
            );

            this.loadCovenantResponsesIntoRow();
          },
          error: (error) => {
            this.savingCovenantResponses = false;

            console.error(
              'Save covenant responses error:',
              error
            );

            this.toast.error(
              error?.error?.message ??
                'CREDIT_SECTION_SUMMARY_TERMS.COVENANT_RESPONSES_SAVE_FAILED'
            );
          }
        });

    this.subscriptions.add(
      saveResponsesSubscription
    );
  }

  private loadSavedCovenantsSummaryRow(): void {
    if (!this.crdtPrpslSeq) {
      return;
    }

    const subscription =
      this.ensureQuestionTypeMap()
        .subscribe(() => this.fetchSavedCovenantsSummaryRow());

    this.subscriptions.add(
      subscription
    );
  }

  private fetchSavedCovenantsSummaryRow(): void {
    if (!this.crdtPrpslSeq) {
      return;
    }

    const savedCovenantsSubscription =
      forkJoin({
        questions:
          this.setup
            .getActQuestionList(this.COVENANTS_QSTNR_SEQ)
            .pipe(
              catchError(() => of([]))
            ),

        savedQuestions:
          this.creditService
            .getCovenantQuestions(this.crdtPrpslSeq)
            .pipe(
              catchError(() => of([]))
            )
      }).subscribe({
        next: (response) => {
          const questions: any[] =
            Array.isArray(response.questions)
              ? response.questions
              : [];

          const savedQuestions =
            (Array.isArray(response.savedQuestions) ? response.savedQuestions : [])
              .filter((saved) => Number(saved?.delFlg ?? 0) !== 1);

          // Nothing saved for this proposal yet — fall back to the
          // covenants saved earlier (get-prev-cnvnt-rsp).
          if (!savedQuestions.length) {
            this.loadPreviousCovenants(questions);
            return;
          }

          const savedSeqs =
            new Set(
              savedQuestions.map(
                (saved) => Number(saved.qstnSeq)
              )
            );

          const selectedQuestions =
            questions
              .filter(
                (question) =>
                  savedSeqs.has(Number(question.qstSeq)) &&
                  String(question.qstStr ?? '').trim()
              )
              .map((question) => {
                const savedMatch =
                  savedQuestions.find(
                    (saved) =>
                      Number(saved?.qstnSeq) ===
                      Number(question.qstSeq)
                  );

                return this.buildCovenantRow(
                  question,
                  savedMatch
                    ? Number(savedMatch.crdtCnvntQstnSeq)
                    : null,
                  true
                );
              });

          const answerOptionsSubscription =
            this.loadCovenantAnswerOptions(
              selectedQuestions
            ).subscribe(() => {
              this.upsertCovenantsSummaryRow(
                selectedQuestions
              );

              this.loadCovenantResponsesIntoRow();
            });

          this.subscriptions.add(
            answerOptionsSubscription
          );
        },
        error: (error) => {
          console.error(
            'Saved covenants summary row load error:',
            error
          );
        }
      });

    this.subscriptions.add(
      savedCovenantsSubscription
    );
  }

  /**
   * Fallback when get-covenant-questions has nothing for this proposal:
   * GET /crdt-prpsl/get-prev-cnvnt-rsp/{crdtPrpslSeq} returns the covenant
   * responses saved earlier, which are patched into the Covenants row the
   * same way saved ones are. Their seqs belong to the earlier record, so
   * they are not kept — this proposal's save creates its own rows.
   */
  private loadPreviousCovenants(questions: any[]): void {
    if (!this.crdtPrpslSeq) {
      return;
    }

    const previousSubscription =
      this.creditService
        .getPreviousCovenantResponses(this.crdtPrpslSeq)
        .pipe(
          catchError((error) => {
            console.error(
              'Previous covenant responses load error:',
              error
            );

            return of(null);
          })
        )
        .subscribe((response) => {
          const previousResponses =
            (Array.isArray(response) ? response : [])
              .filter(
                (item) => Number(item?.delFlg ?? 0) !== 1
              );

          if (!previousResponses.length) {
            return;
          }

          const previousSeqs =
            new Set(
              previousResponses.map((item) =>
                this.previousResponseQuestionSeq(item)
              )
            );

          const selectedQuestions =
            questions
              .filter(
                (question) =>
                  previousSeqs.has(Number(question.qstSeq)) &&
                  String(question.qstStr ?? '').trim()
              )
              .map((question) =>
                this.buildCovenantRow(question, null, true)
              );

          if (!selectedQuestions.length) {
            return;
          }

          const answerOptionsSubscription =
            this.loadCovenantAnswerOptions(
              selectedQuestions
            ).subscribe(() => {
              this.upsertCovenantsSummaryRow(
                selectedQuestions
              );

              this.applyPreviousCovenantResponses(
                previousResponses
              );
            });

          this.subscriptions.add(
            answerOptionsSubscription
          );
        });

    this.subscriptions.add(
      previousSubscription
    );
  }

  /** Question seq of a previous response (`qsntnSeq`, or `qstnSeq` as get-covenant-questions names it). */
  private previousResponseQuestionSeq(item: any): number {
    return Number(item?.qsntnSeq ?? item?.qstnSeq ?? 0);
  }

  /**
   * Fills the Covenants row's answers and remarks from the previous
   * responses — as loadCovenantResponsesIntoRow does for saved ones, but
   * without their seqs and without marking them saved for this proposal.
   */
  private applyPreviousCovenantResponses(
    previousResponses: any[]
  ): void {
    const covenantsRow =
      this.items.find((item) => this.isCovenantsRow(item));

    if (!covenantsRow?.covenantQuestions?.length) {
      return;
    }

    covenantsRow.covenantQuestions.forEach(
      (question) => {
        const previousMatch =
          previousResponses.find(
            (item) =>
              this.previousResponseQuestionSeq(item) ===
              question.qstSeq
          );

        if (!previousMatch) {
          return;
        }

        question.remarks =
          previousMatch.rmrks ?? '';

        if (question.inputType === 'checkbox') {
          question.answerChecked =
            Number(previousMatch.ansVal) === 1;
          return;
        }

        if (question.inputType === 'text') {
          question.answerValue =
            String(previousMatch.ansVal ?? '');
          return;
        }

        const matchedOption =
          question.answerOptions.find(
            (option) =>
              option.answrSeq ===
              Number(previousMatch.ansVal)
          );

        if (matchedOption) {
          question.answerValue =
            matchedOption.answrStr;
        }
      }
    );
  }

  addAllDefaultRows(): void {
    /*
     * Covenants always stays in `availableSummaryTermOptions` so it
     * can be reopened from the dropdown, but it must only ever be
     * added through its own popup flow (openCovenantsPopup), never
     * as a plain createRow() here.
     */
    const availableOptions =
      this.availableSummaryTermOptions.filter(
        (option) =>
          Number(option.value) !==
          this.COVENANTS_REF_CD_SEQ
      );

    if (!availableOptions.length) {
      this.errorMessage =
        this.translate.instant('CREDIT_SECTION_SUMMARY_TERMS.ALL_PARAMETERS_ADDED');
      return;
    }

    availableOptions.forEach(
      (option) => {
        this.items.push(
          this.createRow(
            this.items.length + 1,
            option.label,
            this.getDefaultBlockType(
              option.label
            ),
            Number(option.value)
          )
        );
      }
    );

    this.errorMessage = '';
    this.renumberRows();
  }

  addCustomRow(): void {
    this.items.push({
      serialNo:
        this.items.length + 1,

      code: null,

      description: '',

      expanded: true,

      crdtPrpslBlkSeq: null,

      recommendationBlocks: [
        {
          type: 'paragraph',
          content: ''
        }
      ],

      validationError: ''
    });

    this.renumberRows();
  }


  requestRemoveRow(index: number): void {
    this.pendingDeleteType = 'row';
    this.pendingDeleteRowIndex = index;
    this.deleteMessage =
      'CREDIT_SECTION_SUMMARY_TERMS.DELETE_ROW_CONFIRM';
    this.showDeletePopup = true;
  }

  requestRemoveBlock(
    item: SummaryTermItem,
    blockIndex: number
  ): void {
    this.pendingDeleteType = 'block';
    this.pendingDeleteItem = item;
    this.pendingDeleteBlockIndex = blockIndex;
    this.deleteMessage =
      'CREDIT_SECTION_SUMMARY_TERMS.DELETE_BLOCK_CONFIRM';
    this.showDeletePopup = true;
  }

  requestRemoveCovenantQuestion(
    item: SummaryTermItem,
    question: NonNullable<SummaryTermItem['covenantQuestions']>[number]
  ): void {
    this.pendingDeleteType = 'covenantQuestion';
    this.pendingDeleteItem = item;
    this.pendingDeleteCovenantQuestion = question;
    this.deleteMessage =
      'CREDIT_SECTION_SUMMARY_TERMS.DELETE_COVENANT_CONFIRM';
    this.showDeletePopup = true;
  }

  confirmDelete(): void {
    if (
      this.pendingDeleteType === 'row' &&
      this.pendingDeleteRowIndex !== null
    ) {
      this.removeRow(
        this.pendingDeleteRowIndex
      );
    } else if (
      this.pendingDeleteType === 'block' &&
      this.pendingDeleteItem !== null &&
      this.pendingDeleteBlockIndex !== null
    ) {
      this.removeBlock(
        this.pendingDeleteItem,
        this.pendingDeleteBlockIndex
      );
    } else if (
      this.pendingDeleteType === 'covenantQuestion' &&
      this.pendingDeleteItem !== null &&
      this.pendingDeleteCovenantQuestion !== null
    ) {
      this.removeCovenantQuestion(
        this.pendingDeleteItem,
        this.pendingDeleteCovenantQuestion
      );
    }

    this.pendingDeleteType = null;
    this.pendingDeleteRowIndex = null;
    this.pendingDeleteItem = null;
    this.pendingDeleteBlockIndex = null;
    this.pendingDeleteCovenantQuestion = null;
  }

  private removeRow(index: number): void {
    const removedRow =
      this.items[index];

    if (
      this.activeRecommendationEditor
        ?.item === removedRow
    ) {
      this.activeRecommendationEditor =
        null;
    }

    if (
      removedRow?.crdtPrpslBlkSeq !== null
    ) {
      this.deletedRows.push({
        ...removedRow
      });
    }

    if (
      removedRow &&
      this.isCovenantsRow(removedRow)
    ) {
      this.deleteCovenantResponses(
        removedRow
      );

      this.deleteCovenantQuestionsSelection(
        removedRow
      );
    }

    this.items.splice(index, 1);
    this.renumberRows();
  }

  /**
   * Deleting the Covenants row from the Summary Terms list should
   * also remove its saved answers, since they live separately via
   * `/crdt-prpsl/save-covenant-responses`, not the regular section
   * save/delete flow.
   */
  private deleteCovenantResponses(
    item: SummaryTermItem
  ): void {
    const proposalSequence =
      this.crdtPrpslSeq ??
      this.creditService.getCrdtPrpslSeq();

    // Only saved responses can be deleted; an unsaved one has no seq.
    const questions =
      (item.covenantQuestions ?? []).filter(
        (question) => question.crdtCnvntRspSeq
      );

    if (!proposalSequence || !questions.length) {
      return;
    }

    const responses: SaveCovenantResponseItem[] =
      questions.map((question) => ({
        crdtCnvntRspSeq: question.crdtCnvntRspSeq,
        qsntnSeq: question.qstSeq,
        ansVal: null,
        stsKey: null,
        rmrks: question.remarks ?? '',
        delFlg: 1
      }));

    const deleteResponsesSubscription =
      this.creditService
        .saveCovenantResponses({
          crdtPrpslSeq: proposalSequence,
          responses
        })
        .subscribe({
          next: () => {
            this.toast.success(
              'CREDIT_SECTION_SUMMARY_TERMS.COVENANT_RESPONSES_REMOVED'
            );
          },
          error: (error) => {
            console.error(
              'Delete covenant responses error:',
              error
            );

            this.toast.error(
              error?.error?.message ??
                'CREDIT_SECTION_SUMMARY_TERMS.COVENANT_RESPONSES_REMOVE_FAILED'
            );
          }
        });

    this.subscriptions.add(
      deleteResponsesSubscription
    );
  }

  /**
   * Also deletes the row's covenant question selection via
   * `/crdt-prpsl/save-covenant-questions` (delFlg 1). Without this,
   * the questions would still count as "already added" and stay
   * hidden from the Covenants Questions popup after the row itself
   * is deleted.
   */
  private deleteCovenantQuestionsSelection(
    item: SummaryTermItem
  ): void {
    const proposalSequence =
      this.crdtPrpslSeq ??
      this.creditService.getCrdtPrpslSeq();

    const questions =
      item.covenantQuestions ?? [];

    if (!proposalSequence || !questions.length) {
      return;
    }

    const payloadQuestions =
      questions.map((question) => ({
        crdtCnvntQstnSeq: question.crdtCnvntQstnSeq,
        qstnSeq: question.qstSeq,
        delFlg: 1
      }));

    const deleteQuestionsSubscription =
      this.creditService
        .saveCovenantQuestions({
          crdtPrpslSeq: proposalSequence,
          questions: payloadQuestions
        })
        .subscribe({
          error: (error) => {
            console.error(
              'Delete covenant questions selection error:',
              error
            );
          }
        });

    this.subscriptions.add(
      deleteQuestionsSubscription
    );
  }

  /**
   * Removes a single covenant question from the Covenants row (the
   * per-row Action column delete icon, confirmed via the shared
   * delete popup) — sends just that question back with delFlg 1 so
   * it immediately reappears in the Covenants Questions popup,
   * without touching the other selected questions.
   */
  private removeCovenantQuestion(
    item: SummaryTermItem,
    question: NonNullable<SummaryTermItem['covenantQuestions']>[number]
  ): void {
    const proposalSequence =
      this.crdtPrpslSeq ??
      this.creditService.getCrdtPrpslSeq();

    if (!proposalSequence) {
      this.toast.error(
        'CREDIT_SECTION.PROPOSAL_SEQ_UNAVAILABLE'
      );
      return;
    }

    // Only a saved response is deleted; an unsaved one has no seq.
    if (question.crdtCnvntRspSeq) {
      const removeQuestionResponseSubscription =
        this.creditService
          .saveCovenantResponses({
            crdtPrpslSeq: proposalSequence,
            responses: [
              {
                crdtCnvntRspSeq: question.crdtCnvntRspSeq,
                qsntnSeq: question.qstSeq,
                ansVal: null,
                stsKey: null,
                rmrks: question.remarks ?? '',
                delFlg: 1
              }
            ]
          })
          .subscribe({
            error: (error) => {
              console.error(
                'Remove covenant question response error:',
                error
              );
            }
          });

      this.subscriptions.add(
        removeQuestionResponseSubscription
      );
    }

    const removeQuestionSubscription =
      this.creditService
        .saveCovenantQuestions({
          crdtPrpslSeq: proposalSequence,
          questions: [
            {
              crdtCnvntQstnSeq: question.crdtCnvntQstnSeq,
              qstnSeq: question.qstSeq,
              delFlg: 1
            }
          ]
        })
        .subscribe({
          next: () => {
            item.covenantQuestions =
              (item.covenantQuestions ?? []).filter(
                (existing) =>
                  existing.qstSeq !== question.qstSeq
              );

            this.toast.success(
              'CREDIT_SECTION_SUMMARY_TERMS.COVENANT_QUESTION_REMOVED'
            );

            if (!item.covenantQuestions.length) {
              this.items =
                this.items.filter(
                  (existing) => existing !== item
                );

              this.renumberRows();
            }
          },
          error: (error) => {
            console.error(
              'Remove covenant question error:',
              error
            );

            this.toast.error(
              error?.error?.message ??
                'CREDIT_SECTION_SUMMARY_TERMS.COVENANT_QUESTION_REMOVE_FAILED'
            );
          }
        });

    this.subscriptions.add(
      removeQuestionSubscription
    );
  }

  addBlock(
    item: SummaryTermItem,
    type: SummaryRecommendationBlockType
  ): void {
    item.recommendationBlocks.push({
      type,
      content: ''
    });
  }

  onBlockDrop(
    item: SummaryTermItem,
    event: CdkDragDrop<unknown>
  ): void {
    if (event.previousIndex === event.currentIndex) {
      return;
    }

    moveItemInArray(
      item.recommendationBlocks,
      event.previousIndex,
      event.currentIndex
    );

    // The stored cursor position refers to a block index that has now moved.
    if (
      this.activeRecommendationEditor
        ?.item === item
    ) {
      this.activeRecommendationEditor =
        null;
    }
  }

  private removeBlock(
    item: SummaryTermItem,
    blockIndex: number
  ): void {
    item.recommendationBlocks.splice(
      blockIndex,
      1
    );

    if (
      this.activeRecommendationEditor
        ?.item === item
    ) {
      this.activeRecommendationEditor =
        null;
    }
  }

  insertSelectedReference(
    item: SummaryTermItem,
    rowIndex: number
  ): void {
    const selectedKey =
      String(
        this.selectedReferenceKeyByRow[
          rowIndex
        ] ?? ''
      ).trim();

    if (!selectedKey) {
      this.errorMessage =
        this.translate.instant('CREDIT_SECTION_SUMMARY_TERMS.SELECT_TERM_VALUE_FIRST');
      return;
    }

    const reference =
      this.termParameterReferences.find(
        (itemReference) =>
          itemReference.key ===
          selectedKey
      );

    if (!reference) {
      this.errorMessage =
        this.translate.instant('CREDIT_SECTION_SUMMARY_TERMS.TERM_VALUE_UNAVAILABLE');
      return;
    }

    this.errorMessage = '';

    this.insertReferenceValue(
      item,
      reference
    );

    /*
     * Reset dropdown after insertion so the
     * user can clearly choose the next value.
     */
    this.selectedReferenceKeyByRow[
      rowIndex
    ] = '';
  }

  getReferenceGroupLabelKey(
    group: TermParameterGroup
  ): string {
    return (
      TERM_REFERENCE_GROUP_LABEL_KEYS[group.key] ??
      group.label
    );
  }

  trackByReferenceGroup(
    _index: number,
    group: TermParameterGroup
  ): string {
    return group.key;
  }

  insertReferenceIntoActiveEditor(
    reference:
      TermParameterReference
  ): void {
    const activeItem =
      this.activeRecommendationEditor
        ?.item;

    if (activeItem) {
      this.insertReferenceValue(
        activeItem,
        reference
      );
      return;
    }

    const targetItem =
      this.items.find(
        (item) => item.expanded
      ) ??
      this.items[0];

    if (!targetItem) {
      this.errorMessage =
        this.translate.instant('CREDIT_SECTION_SUMMARY_TERMS.ADD_ROW_BEFORE_INSERT');
      return;
    }

    this.insertReferenceValue(
      targetItem,
      reference
    );
  }

  insertReferenceValue(
    item: SummaryTermItem,
    reference:
      TermParameterReference
  ): void {
    const activeEditor =
      this.activeRecommendationEditor;

    let blockIndex = -1;
    let targetBlock:
      SummaryRecommendationBlock | null =
      null;

    if (
      activeEditor &&
      activeEditor.item === item &&
      item.recommendationBlocks[
        activeEditor.blockIndex
      ]
    ) {
      blockIndex =
        activeEditor.blockIndex;

      targetBlock =
        item.recommendationBlocks[
          blockIndex
        ];
    }

    if (!targetBlock) {
      blockIndex =
        item.recommendationBlocks.findIndex(
          (block) =>
            block.type === 'paragraph'
        );

      if (blockIndex === -1) {
        item.recommendationBlocks.push({
          type: 'paragraph',
          content: ''
        });

        blockIndex =
          item.recommendationBlocks.length - 1;
      }

      targetBlock =
        item.recommendationBlocks[
          blockIndex
        ];
    }

    const currentContent =
      String(
        targetBlock.content ?? ''
      );

    const useActiveSelection =
      activeEditor &&
      activeEditor.item === item &&
      activeEditor.blockIndex ===
        blockIndex;

    const selectionStart =
      useActiveSelection
        ? this.clampCursorPosition(
            activeEditor.selectionStart,
            currentContent.length
          )
        : currentContent.length;

    const selectionEnd =
      useActiveSelection
        ? this.clampCursorPosition(
            activeEditor.selectionEnd,
            currentContent.length
          )
        : currentContent.length;

    const referenceText =
      `${reference.label}: ${reference.value}`;

    if (targetBlock.type === 'paragraph') {
      const richEditor =
        this.findRecommendationRichEditor(
          item,
          blockIndex
        );

      this.activeRecommendationEditor = {
        item,
        blockIndex,
        selectionStart: 0,
        selectionEnd: 0
      };

      if (richEditor?.insertText(referenceText)) {
        return;
      }

      // Editor not rendered (e.g. collapsed row): append to the model.
      if (isRichTextHtml(currentContent)) {
        targetBlock.content =
          currentContent +
          plainTextToRichText(referenceText);
        return;
      }
    }

    const beforeText =
      currentContent.slice(
        0,
        selectionStart
      );

    const afterText =
      currentContent.slice(
        selectionEnd
      );

    const leadingSpace =
      beforeText.length > 0 &&
      !/\s$/.test(beforeText)
        ? ' '
        : '';

    const trailingSpace =
      afterText.length > 0 &&
      !/^\s/.test(afterText)
        ? ' '
        : '';

    targetBlock.content =
      beforeText +
      leadingSpace +
      referenceText +
      trailingSpace +
      afterText;

    const newCursorPosition =
      beforeText.length +
      leadingSpace.length +
      referenceText.length +
      trailingSpace.length;

    this.activeRecommendationEditor = {
      item,
      blockIndex,
      selectionStart:
        newCursorPosition,
      selectionEnd:
        newCursorPosition
    };

    this.focusRecommendationEditor(
      item,
      blockIndex,
      newCursorPosition
    );
  }

  onRecommendationEditorFocus(
    item: SummaryTermItem,
    blockIndex: number,
    event: Event
  ): void {
    this.captureRecommendationSelection(
      item,
      blockIndex,
      event
    );
  }

  /** Rich text paragraph blocks track their own cursor; just remember which block is active. */
  onRecommendationRichEditorFocus(
    item: SummaryTermItem,
    blockIndex: number
  ): void {
    this.activeRecommendationEditor = {
      item,
      blockIndex,
      selectionStart: 0,
      selectionEnd: 0
    };
  }

  /** Row-scoped number for a numbered-list line: 1.1, 1.2… under row "1.". */
  numberedLabel(
    item: SummaryTermItem,
    block: SummaryRecommendationBlock,
    lineIndex: number
  ): string {
    return scopedListLabel(
      item.serialNo,
      item.recommendationBlocks,
      block,
      lineIndex,
      (candidate) =>
        candidate.type === 'numbered-list',
      (candidate) =>
        this.getLines(candidate.content).length
    );
  }

  private findRecommendationRichEditor(
    item: SummaryTermItem,
    blockIndex: number
  ): RichTextEditor | undefined {
    const editorId =
      this.getRecommendationEditorId(
        this.items.indexOf(item),
        blockIndex
      );

    return this.recommendationRichEditors?.find(
      (editor) =>
        editor.hostElement.id === editorId
    );
  }

  onRecommendationEditorSelection(
    item: SummaryTermItem,
    blockIndex: number,
    event: Event
  ): void {
    this.captureRecommendationSelection(
      item,
      blockIndex,
      event
    );
  }

  getLines(content: string): string[] {
    return String(content ?? '')
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);
  }

  trackByRow(
    index: number,
    item: SummaryTermItem
  ): number | string {
    return (
      item.crdtPrpslBlkSeq ??
      `new-${index}`
    );
  }

  trackByReference(
    index: number,
    item: TermParameterReference
  ): string {
    return item.key;
  }

  trackByBlock(
    _index: number,
    block: SummaryRecommendationBlock
  ): SummaryRecommendationBlock {
    return block;
  }

  private captureRecommendationSelection(
    item: SummaryTermItem,
    blockIndex: number,
    event: Event
  ): void {
    const textarea =
      event.target as
        HTMLTextAreaElement | null;

    if (!textarea) {
      return;
    }

    this.activeRecommendationEditor = {
      item,
      blockIndex,
      selectionStart:
        textarea.selectionStart ??
        textarea.value.length,
      selectionEnd:
        textarea.selectionEnd ??
        textarea.value.length
    };
  }

  private focusRecommendationEditor(
    item: SummaryTermItem,
    blockIndex: number,
    cursorPosition: number
  ): void {
    const rowIndex =
      this.items.indexOf(item);

    if (rowIndex < 0) {
      return;
    }

    window.setTimeout(() => {
      const editor =
        document.getElementById(
          this.getRecommendationEditorId(
            rowIndex,
            blockIndex
          )
        ) as
          HTMLTextAreaElement | null;

      if (!editor) {
        return;
      }

      editor.focus();

      const normalizedCursor =
        this.clampCursorPosition(
          cursorPosition,
          editor.value.length
        );

      editor.setSelectionRange(
        normalizedCursor,
        normalizedCursor
      );
    });
  }

  getRecommendationEditorId(
    rowIndex: number,
    blockIndex: number
  ): string {
    return (
      'summaryRecommendationEditor_' +
      rowIndex +
      '_' +
      blockIndex
    );
  }

  private clampCursorPosition(
    position: number,
    contentLength: number
  ): number {
    const normalizedPosition =
      Number(position);

    if (
      !Number.isFinite(
        normalizedPosition
      )
    ) {
      return contentLength;
    }

    return Math.max(
      0,
      Math.min(
        Math.trunc(
          normalizedPosition
        ),
        contentLength
      )
    );
  }

  /**
   * @param requireSavedCovenants "Save and Continue" only: covenant answers
   * must already be saved through "Save Covenants". "Save Draft" skips it.
   */
  private validateRows(requireSavedCovenants = false): boolean {
    let valid = true;

    this.items.forEach((row) => {
      row.validationError = '';

      if (!row.description.trim()) {
        row.validationError =
          this.translate.instant('CREDIT_SECTION_SUMMARY_TERMS.DESCRIPTION_REQUIRED');

        row.expanded = true;
        valid = false;
        return;
      }

      const validBlocks =
        row.recommendationBlocks.filter(
          (block) =>
            block.content.trim()
        );

      if (!validBlocks.length) {
        row.validationError =
          this.translate.instant('CREDIT_SECTION_SUMMARY_TERMS.RECOMMENDATION_REQUIRED');

        row.expanded = true;
        valid = false;
        return;
      }

      const hasMissingCovenantAnswer =
        (row.covenantQuestions ?? []).some(
          (question) => this.isCovenantAnswerMissing(question)
        );

      if (hasMissingCovenantAnswer) {
        row.validationError =
          this.translate.instant('CREDIT_SECTION_SUMMARY_TERMS.ANSWER_ALL_COVENANTS');

        row.expanded = true;
        valid = false;
        return;
      }

      if (requireSavedCovenants && this.hasUnsavedCovenantAnswers(row)) {
        row.validationError =
          this.translate.instant('CREDIT_SECTION_SUMMARY_TERMS.SAVE_COVENANTS_FIRST');

        row.expanded = true;
        valid = false;
      }
    });

    return valid;
  }

  private attachMissingPicklistCodes(): void {
    if (
      !this.summaryTermOptions.length ||
      !this.items.length
    ) {
      return;
    }

    this.items.forEach(
      (item) => {
        if (
          this.toNullableNumber(
            item.code
          ) !== null
        ) {
          return;
        }

        const matchedOption =
          this.findSummaryTermOptionByLabel(
            item.description
          );

        if (matchedOption) {
          item.code =
            Number(
              matchedOption.value
            );

          item.description =
            matchedOption.label;
        }
      }
    );
  }

  private findSummaryTermOptionByLabel(
    description: string
  ): DropdownOption | undefined {
    const normalizedDescription =
      String(
        description ?? ''
      )
        .trim()
        .toLowerCase();

    if (!normalizedDescription) {
      return undefined;
    }

    return this.summaryTermOptions.find(
      (option) =>
        String(
          option.label ?? ''
        )
          .trim()
          .toLowerCase() ===
        normalizedDescription
    );
  }


  private createRow(
    serialNo: number,
    description: string,
    blockType:
      SummaryRecommendationBlockType,
    code: number | null = null
  ): SummaryTermItem {
    return {
      serialNo,
      code,
      description,
      expanded: serialNo === 1,
      crdtPrpslBlkSeq: null,
      recommendationBlocks: [
        {
          type: blockType,
          content: ''
        }
      ],
      validationError: ''
    };
  }

  private getDefaultBlockType(
    description: string
  ): SummaryRecommendationBlockType {
    const normalized =
      description.toLowerCase();

    if (
      normalized.includes('covenant') ||
      normalized.includes('outcome')
    ) {
      return 'numbered-list';
    }

    if (
      normalized.includes('withdrawal')
    ) {
      return 'paragraph';
    }

    return 'bullet-list';
  }

  private buildTermParameterReferences():
    void {
    const response =
      this.rawTermsResponse;

    if (!response) {
      this.termParameterReferences = [];
      this.termParameterGroups = [];
      return;
    }

    const references:
      TermParameterReference[] = [];

    /*
     * =============================
     * EXPOSURE
     * =============================
     */
    this.addReference(
      references,
      'proposed-exposure',
      'Proposed Exposure',
      this.formatAmount(
        response.prpsdExposure
      ),
      1,
      'exposure'
    );

    /*
     * =============================
     * REPAYMENT
     * =============================
     */
    this.addReference(
      references,
      'grace-period',
      'Grace Period',
      this.formatNumber(
        response.grcPrd,
        'Months'
      ),
      10,
      'repayment'
    );

    this.addReference(
      references,
      'number-of-installments',
      'Number of Installments',
      this.formatPlainNumber(
        response.noOfInstlmnt
      ),
      11,
      'repayment'
    );

    this.addReference(
      references,
      'repayment-frequency',
      'Repayment Frequency',
      this.getPicklistLabel(
        response.rpymntFrqncy,
        this.frequencyOptions
      ),
      12,
      'repayment'
    );

    this.addReference(
      references,
      'repayment-start-date',
      'Repayment Start Date',
      this.formatDate(
        response.rpStrtDt
      ),
      13,
      'repayment'
    );

    this.addReference(
      references,
      'repayment-end-date',
      'Repayment End Date',
      this.formatDate(
        response.rpEndDt
      ),
      14,
      'repayment'
    );

    /*
     * =============================
     * FEES & CHARGES
     * =============================
     */
    this.addReference(
      references,
      'upfront-fee',
      'Upfront Fee',
      this.formatPercentage(
        response.upFrntFee
      ),
      20,
      'fees'
    );

    this.addReference(
      references,
      'credit-insurance-fee',
      'Credit Insurance Fee',
      this.formatPercentage(
        response.crdtEnsrFee
      ),
      21,
      'fees'
    );

    const facilityTerms =
      Array.isArray(
        response.crdtTrmParamsLst
      )
        ? response.crdtTrmParamsLst.filter(
            (term) =>
              Number(
                term?.delFlg ?? 0
              ) !== 1
          )
        : [];

    facilityTerms.forEach(
      (
        term: CreditFacilityTerm,
        facilityIndex: number
      ) => {
        const prefix =
          facilityTerms.length > 1
            ? `Facility ${facilityIndex + 1} - `
            : '';

        const sortBase =
          100 + facilityIndex * 100;

        /*
         * =============================
         * FACILITY
         * =============================
         */
        this.addReference(
          references,
          `facility-type-${facilityIndex}`,
          `${prefix}Facility Type`,
          this.getPicklistLabel(
            term.typ,
            this.termParameterOptions
          ),
          sortBase + 1,
          'facility'
        );

        this.addReference(
          references,
          `renewable-amount-${facilityIndex}`,
          `${prefix}Renewable Amount`,
          this.formatAmount(
            term.rnwAblAmnt
          ),
          sortBase + 2,
          'facility'
        );

        /*
         * =============================
         * DISBURSEMENT
         * =============================
         */
        this.addReference(
          references,
          `disbursement-amount-${facilityIndex}`,
          `${prefix}Disbursement Amount`,
          this.formatAmount(
            term.dsbmntAmount
          ),
          sortBase + 10,
          'disbursement'
        );

        this.addReference(
          references,
          `disbursement-start-${facilityIndex}`,
          `${prefix}Disbursement Start Date`,
          this.formatDate(
            term.dsbStrtDt
          ),
          sortBase + 11,
          'disbursement'
        );

        this.addReference(
          references,
          `disbursement-end-${facilityIndex}`,
          `${prefix}Disbursement End Date`,
          this.formatDate(
            term.dsbEndDt
          ),
          sortBase + 12,
          'disbursement'
        );

        /*
         * =============================
         * PRICING / KIBOR
         * =============================
         */
        this.addReference(
          references,
          `spread-rate-${facilityIndex}`,
          `${prefix}Spread Rate`,
          this.formatPercentage(
            term.sprdRt
          ),
          sortBase + 20,
          'pricing'
        );

        this.addReference(
          references,
          `kibor-start-${facilityIndex}`,
          `${prefix}KIBOR Start`,
          this.getPicklistLabel(
            term.vKbrStrt,
            this.termParameterOptions
          ),
          sortBase + 21,
          'pricing'
        );

        this.addReference(
          references,
          `kibor-reset-period-${facilityIndex}`,
          `${prefix}KIBOR Reset Period`,
          this.getPicklistLabel(
            term.kbrRstPrd,
            this.termParameterOptions
          ),
          sortBase + 22,
          'pricing'
        );

        /*
         * =============================
         * GUARANTEE / SECURITY
         * =============================
         */
        const guarantors =
          Array.isArray(
            term.crdtTrmParamsGrntrList
          )
            ? term.crdtTrmParamsGrntrList.filter(
                (guarantor) =>
                  Number(
                    guarantor?.delFlg ?? 0
                  ) !== 1
              )
            : [];

        guarantors.forEach(
          (
            guarantor: CreditGuarantor,
            guarantorIndex: number
          ) => {
            const guarantorLabel =
              guarantors.length > 1
                ? `Guarantor ${guarantorIndex + 1}`
                : 'Guarantor';

            this.addReference(
              references,
              `guarantor-name-${facilityIndex}-${guarantorIndex}`,
              `${prefix}${guarantorLabel}`,
              String(
                guarantor.grntrNm ?? ''
              ).trim(),
              sortBase + 30 +
                guarantorIndex * 2,
              'guarantee'
            );

            this.addReference(
              references,
              `guarantor-share-${facilityIndex}-${guarantorIndex}`,
              `${prefix}${guarantorLabel} Share`,
              this.formatPercentage(
                guarantor.share
              ),
              sortBase + 31 +
                guarantorIndex * 2,
              'guarantee'
            );
          }
        );
      }
    );

    this.termParameterReferences =
      references.sort(
        (first, second) =>
          first.sortOrder -
          second.sortOrder
      );

    this.buildTermParameterGroups();
  }

  private buildTermParameterGroups():
    void {
    const grouped =
      new Map<
        TermReferenceGroupKey,
        TermParameterReference[]
      >();

    this.termParameterReferences.forEach(
      (reference) => {
        const existing =
          grouped.get(
            reference.group
          ) ?? [];

        existing.push(
          reference
        );

        grouped.set(
          reference.group,
          existing
        );
      }
    );

    this.termParameterGroups =
      Array.from(
        grouped.entries()
      )
        .map(
          ([
            key,
            references
          ]): TermParameterGroup => {
            const meta =
              TERM_REFERENCE_GROUP_META[
                key
              ];

            return {
              key,
              label:
                meta.label,
              description:
                meta.description,
              sortOrder:
                meta.sortOrder,
              references:
                references.sort(
                  (
                    first,
                    second
                  ) =>
                    first.sortOrder -
                    second.sortOrder
                )
            };
          }
        )
        .sort(
          (first, second) =>
            first.sortOrder -
            second.sortOrder
        );
  }

  private addReference(
    references:
      TermParameterReference[],
    key: string,
    label: string,
    value: string,
    sortOrder: number,
    group: TermReferenceGroupKey
  ): void {
    const normalizedValue =
      String(value ?? '').trim();

    if (
      !normalizedValue ||
      normalizedValue ===
        'Not entered'
    ) {
      return;
    }

    references.push({
      key,
      label,
      value:
        normalizedValue,
      sortOrder,
      group
    });
  }

  private extractTermParameterResponse(
    response: any
  ): CreditTermParametersResponse | null {
    const possibleValues = [
      response?.data,
      response
    ];

    const result =
      possibleValues.find(
        (value) =>
          value &&
          typeof value === 'object' &&
          !Array.isArray(value)
      );

    return result
      ? result as
          CreditTermParametersResponse
      : null;
  }

  private getPicklistLabel(
    value: unknown,
    options: DropdownOption[]
  ): string {
    const numericValue =
      this.toNullableNumber(value);

    if (numericValue === null) {
      return 'Not entered';
    }

    const option =
      options.find(
        (item) =>
          Number(item.value) ===
          numericValue
      );

    return (
      option?.label ??
      String(numericValue)
    );
  }

  private formatPlainNumber(
    value: unknown
  ): string {
    const numericValue =
      this.toNullableNumber(value);

    if (numericValue === null) {
      return 'Not entered';
    }

    return new Intl.NumberFormat(
      'en-PK',
      {
        maximumFractionDigits: 2
      }
    ).format(numericValue);
  }

  private formatNumber(
    value: unknown,
    suffix: string
  ): string {
    const formattedValue =
      this.formatPlainNumber(value);

    if (
      formattedValue ===
      'Not entered'
    ) {
      return formattedValue;
    }

    return `${formattedValue} ${suffix}`;
  }

  private formatAmount(
    value: unknown
  ): string {
    const numericValue =
      this.toNullableNumber(value);

    if (numericValue === null) {
      return 'Not entered';
    }

    return `PKR ${new Intl.NumberFormat(
      'en-PK',
      {
        maximumFractionDigits: 2
      }
    ).format(numericValue)} M`;
  }

  private formatPercentage(
    value: unknown
  ): string {
    const numericValue =
      this.toNullableNumber(value);

    if (numericValue === null) {
      return 'Not entered';
    }

    return `${new Intl.NumberFormat(
      'en-PK',
      {
        maximumFractionDigits: 2
      }
    ).format(numericValue)}%`;
  }

  private formatDate(
    value: unknown
  ): string {
    const rawDate =
      String(value ?? '').trim();

    if (!rawDate) {
      return 'Not entered';
    }

    const date =
      new Date(
        `${rawDate}T00:00:00`
      );

    if (
      Number.isNaN(
        date.getTime()
      )
    ) {
      return rawDate;
    }

    return new Intl.DateTimeFormat(
      'en-GB',
      {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      }
    ).format(date);
  }

  private extractPreviousMaxExposure(
    response: any
  ): number | null {
    const possibleValues = [
      response?.prvsMaxExpsr,
      response?.previousMaxExposure,
      response?.maxPrvsExpsr,
      response?.maxExposure,
      response?.val,

      response?.data?.prvsMaxExpsr,
      response?.data
        ?.previousMaxExposure,
      response?.data?.maxPrvsExpsr,
      response?.data?.maxExposure,
      response?.data?.val,

      typeof response?.data === 'number'
        ? response.data
        : null,

      typeof response === 'number'
        ? response
        : null
    ];

    for (
      const value
      of possibleValues
    ) {
      const numberValue =
        this.toNullableNumber(value);

      if (numberValue !== null) {
        return numberValue;
      }
    }

    return null;
  }

  private extractResponseList(
    response: any
  ): any[] {
    const possibleLists = [
      response?.blockList,
      response?.data?.blockList,
      response?.data?.content,
      response?.content,
      response?.data,
      response
    ];

    const list =
      possibleLists.find(
        (value) =>
          Array.isArray(value)
      );

    return Array.isArray(list)
      ? list
      : [];
  }

  private parseJsonData(
    jsonData: any
  ): any {
    if (Array.isArray(jsonData)) {
      return jsonData[0] ?? {};
    }

    if (
      jsonData &&
      typeof jsonData === 'object'
    ) {
      return jsonData;
    }

    if (
      typeof jsonData === 'string'
    ) {
      try {
        const parsed =
          JSON.parse(jsonData);

        return Array.isArray(parsed)
          ? parsed[0] ?? {}
          : parsed;
      } catch {
        return {};
      }
    }

    return {};
  }

  private mapRecommendationBlocks(
    recommendationBlocks: any
  ): SummaryRecommendationBlock[] {
    if (
      !Array.isArray(
        recommendationBlocks
      )
    ) {
      return [];
    }

    return recommendationBlocks.map(
      (
        block: any
      ): SummaryRecommendationBlock => ({
        type:
          this.normalizeBlockType(
            block?.type
          ),

        content:
          block?.type === 'paragraph'
            ? block?.content ?? ''
            : Array.isArray(block?.items)
              ? block.items.join('\n')
              : block?.content ?? ''
      })
    );
  }

  private normalizeBlockType(
    type: string
  ): SummaryRecommendationBlockType {
    if (
      type === 'bullet-list' ||
      type === 'numbered-list' ||
      type === 'paragraph'
    ) {
      return type;
    }

    return 'paragraph';
  }

  private renumberRows(): void {
    this.items.forEach(
      (item, index) => {
        item.serialNo = index + 1;
      }
    );
  }

  private toNullableNumber(
    value: unknown
  ): number | null {
    if (
      value === null ||
      value === undefined ||
      value === ''
    ) {
      return null;
    }

    const numberValue =
      Number(value);

    return Number.isFinite(numberValue)
      ? numberValue
      : null;
  }

  private findRouteParameter(
    parameterNames: string[]
  ): string | null {
    let currentRoute:
      ActivatedRoute | null =
      this.route;

    while (currentRoute) {
      for (
        const parameterName
        of parameterNames
      ) {
        const value =
          currentRoute.snapshot
            .paramMap
            .get(parameterName);

        if (value !== null) {
          return value;
        }
      }

      currentRoute =
        currentRoute.parent;
    }

    return null;
  }
}